<template>
  <div :class="dashboardClass">
    <stats v-if="!appointmentId"/>
    <lots-list :appointment-id="appointmentId"/>
  </div>
</template>

<script>
import parse from 'date-fns/parse';
import gql from 'graphql-tag';

import Stats from './components/Stats';
import LotsList from './components/LotsList';

const statusesMatch = {
  ACTIVE: 'active',
  CONFIRMATION: 'bids_confirm',
  WAITING_FOR_CLIENT: 'wait_client',
};

const CAMPAIGN_ID_TEST = '1407187';
const testAuctionFilter = {
  field: 'a.campaign',
  operator: 'NEQ',
  value: CAMPAIGN_ID_TEST,
};

export default {
  components: {
    Stats,
    LotsList,
  },
  apollo: {
    auction: {
      skip: true,
      fetchPolicy: 'network-only',
      query() {
        return gql`
          query($id: Int!) {
            auction(id: $id) {
              id
              secondsLeft
              bestBidPrice
              timeFinish
              priceClient
              sellingPriceMeter
              bidsCount
              status
            }
          }
        `;
      },
      result({ data }) {
        if (!data.auction) return;

        const auction = this.currentAuctions.find(a => a.id === data.auction.id);
        if (!auction) return;

        this.$store.commit('updateAuction', {
          ...auction,
          ...this.parseSingle(data.auction),
        });
      },
    },
    auctions: {
      fetchPolicy: 'network-only',
      query: gql`
        query($filters: AuctionInput!, $pageSize: Int) {
          auctions(
            pageSize: $pageSize,
            sorting: "id desc",
            filters: $filters
          ) {
            data {
              id
              status
              timeStart
              timeFinish
              bestBidPrice
              startPrice
              priceClient
              carId
              finished
              secondsLeft
              bidsCount
              sellingPriceMeter
              car {
                id
                brandId
                carBrand {
                  name
                }
                photoMain
                modelId {
                  title
                }
                year
                modificationId {
                  fullTitle
                }
                appointmentId
                appointment {
                  id
                }
              }
            }
          }
        }
      `,
      variables() {
        const customFilters = [
          {
            field: 'auc.status',
            operator: 'IN',
            value: 'ACTIVE,CONFIRMATION,WAITING_FOR_CLIENT',
          }, {
            field: 'closed',
            operator: 'NEQ',
            value: '1',
          }];

        if (this.appointmentId) {
          customFilters.push({
            field: 'a.id',
            operator: 'EQ',
            value: `${this.appointmentId}`,
          });
        } else if (this.$route.query.test !== '1') {
          customFilters.push(testAuctionFilter);
        }

        return {
          filters: { customFilters },
          pageSize: this.appointmentId ? 1 : 9,
        };
      },
      result({ data }) {
        const auctions = data.auctions;
        if (!auctions.data) return;


        if (!this.initial) {
          const auction = this.parseAuction(auctions.data[0]);
          if (!this.currentAuctions.find(a => a.id === auction.id)) {
            this.currentAuctions.push(auction);
          }
          this.$store.commit('updateAuction', auction);

          this.isLoadingAuctions = false;
          return;
        }

        this.initial = false;
        this.$store.commit('setAuctionsStatus', 'ready');
        this.subscriptionUpdated(this.$apollo);

        auctions.data.forEach((a) => {
          this.currentAuctions.push(this.parseAuction(a));
        });
        this.loaded = true;
        this.isLoadingAuctions = false;
      },
      watchLoading(isLoading) {
        if (isLoading) {
          this.isLoadingAuctions = isLoading;
        }
      },
      error() {
        this.$store.commit('setAuctionsStatus', 'error');
      },
    },
    stats: {
      query: gql`{
        stats: auctionStats {
          bids
          auctions
        }
      }`,
      fetchPolicy: 'network-only',
      result({ data }) {
        this.$store.commit('setStats', data.stats);
      },
      error() {
        this.$store.commit('setStatsStatus', 'error');
      },
    },
  },
  data() {
    return {
      loaded: false,
      currentAuctions: [],
      initial: true,
      refetchAuctionQueue: [],
      isLoadingAuctions: false,
    };
  },
  computed: {
    appointmentId() {
      return parseInt(this.$route.query.id, 10) || 0;
    },
    dashboardClass() {
      return {
        dashboard: true,
        'dashboard--single-lot': !!this.appointmentId,
      };
    },
    allLoaded() {
      return this.loaded;
    },
    refetchStart() {
      return this.refetchAuctionQueue.length > 0 && !this.isLoadingAuctions;
    },
  },
  watch: {
    allLoaded(v) {
      if (!v) return;
      this.$store.dispatch('init');

      this.$store.dispatch('initAuctions', {
        auctions: this.currentAuctions,
      });
      this.loaded = true;
    },
    refetchStart(v) {
      if (v) this.refetchAuctions();
    },
  },
  methods: {
    parseSingle(a) {
      return {
        auctionId: a.id,
        id: a.id,
        timeFinish: (this.timestamp(parse(a.timeFinish))) / 1000,
        bestBidPrice: a.bestBidPrice,
        fullPrice: a.priceClient,
        sellingPriceMeter: a.sellingPriceMeter,
        bidsCount: a.bidsCount,
        status: statusesMatch[a.status],
      };
    },
    parseAuction(a) {
      return {
        auctionId: a.id,
        id: a.id,
        appointmentId: a.car.appointment.id,
        carId: a.car.id,
        brand: a.car.carBrand.name,
        model: a.car.modelId.title,
        year: a.car.year,
        carPhoto: a.car.photoMain.replace('type_x', 'type_d'),
        timeStart: (this.timestamp(parse(a.timeStart))) / 1000,
        timeFinish: (this.timestamp(parse(a.timeFinish))) / 1000,
        bestBidPrice: a.bestBidPrice,
        fullPrice: a.priceClient,
        startPrice: a.startPrice,
        sellingPriceMeter: a.sellingPriceMeter,
        bidsCount: a.bidsCount,
        status: statusesMatch[a.status],
      };
    },
    subscriptionUpdated($apollo) {
      const created = $apollo.subscribe({
        query: gql`subscription auction {
          auction {
            id
          }
        }`,
      });

      created.subscribe({
        next: async ({ data }) => {
          this.refetchAuctionQueue.push(data.auction.id.toString());
        },
      });

      const bidMade = $apollo.subscribe({
        query: gql`subscription BidMade {
          BidMade {
            id
          }
        }`,
      });

      bidMade.subscribe({
        next: async ({ data }) => {
          await $apollo.queries.auction.refetch({ id: data.BidMade.id });
          // eslint-disable-next-line
          $apollo.queries.auction.skip = false;
        },
      });

      const observer = $apollo.subscribe({
        query: gql`subscription AuctionUpdated {
          AuctionUpdated {
            id
            field
            value
          }
        }`,
      });

      observer.subscribe({
        next: ({ data }) => {
          const {
            value, id, field,
          } = data.AuctionUpdated;

          if (!value) return;

          const f = field === 'bestBidPrice' ? 'bestBidPrice minNextBid' : field;
          const d = field === 'bestBidPrice'
            ? {
              bestBidPrice: parseInt(value, 10),
            }
            : { [field]: value };

          $apollo.getClient().writeQuery({
            query: gql`
              query {
                auction {
                  id
                  ${f}
                }
              }`,
            data: {
              auction: {
                id,
                ...d,
                __typename: 'Auction',
              },
            },
          });
        },
      });
    },
    refetchAuctions() {
      this.$apollo.queries.auctions.refetch({ filters: { customFilters: [
        { field: 'id',
          operator: 'EQ',
          value: this.refetchAuctionQueue.shift(),
        },
        testAuctionFilter] },
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/App.vue